import React, {useCallback, useMemo, useState, useTransition} from "react";
import {Box, Chip, InputAdornment, TextField, Typography} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {useTranslation} from "react-i18next";
import {
    groupDataSourceOptions,
    resolveCatalogEntry,
} from "../../../catalog";
import {ImportingDataTypes} from "../../../types";
import CategorySection from "./category-section";

const DataSourceCatalog = ({
                               options,
                               catalogContext,
                               onSelect,
                               loading,
                               noCodeGroups,
                           }) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.settingsImport.dataSourceCatalog'});
    const [search, setSearch] = useState('');
    const [filterQuery, setFilterQuery] = useState('');
    const [, startTransition] = useTransition();
    const [expandedId, setExpandedId] = useState(null);
    const [activeCategory, setActiveCategory] = useState(null);

    const handleSearchChange = useCallback((e) => {
        const value = e.target.value;
        setSearch(value);
        startTransition(() => {
            setFilterQuery(value);
        });
    }, []);

    const filteredOptions = useMemo(() => {
        const query = filterQuery.trim().toLowerCase();
        if (!query) {
            return options;
        }
        return options.filter((option) => {
            const entry = resolveCatalogEntry(option, catalogContext, t, {noCodeGroups});
            const haystack = [
                entry.title,
                entry.description,
                entry.detail,
                entry.effect,
                ...(entry.keywords ?? []),
            ].join(' ').toLowerCase();
            return haystack.includes(query);
        });
    }, [options, filterQuery, catalogContext, t, noCodeGroups]);

    const expandedGroupedOptions = useMemo(
        () => groupDataSourceOptions(filteredOptions, {expandMemberships: true}),
        [filteredOptions],
    );

    const primaryGroupedOptions = useMemo(
        () => groupDataSourceOptions(filteredOptions, {expandMemberships: false}),
        [filteredOptions],
    );

    /**
     * All / search: one card per dataset (primary category only).
     * Specific category chip: include multi-category datasets that belong there.
     */
    const visibleGroups = useMemo(
        () => (activeCategory
            ? expandedGroupedOptions.filter((g) => g.group === activeCategory)
            : primaryGroupedOptions),
        [expandedGroupedOptions, primaryGroupedOptions, activeCategory],
    );

    const resolveEntry = useCallback((option) => {
        const needsDynamicData = option.id === ImportingDataTypes.partTechnicalInformation
            || option.id === ImportingDataTypes.partPriceLevels
            || option.id === ImportingDataTypes.productCodeAssignment;
        return resolveCatalogEntry(option, catalogContext, t, {
            columnsLoading: loading && needsDynamicData,
            noCodeGroups: option.id === ImportingDataTypes.productCodeAssignment && noCodeGroups,
        });
    }, [catalogContext, t, loading, noCodeGroups]);

    const handleExpand = useCallback((id, isExpanded) => {
        setExpandedId(isExpanded ? id : null);
    }, []);

    const handleSelect = useCallback((option) => {
        onSelect(null, option);
    }, [onSelect]);

    const totalFilteredCount = useMemo(
        () => filteredOptions.length,
        [filteredOptions],
    );

    const renderCategoryChip = (groupKey, label, count, isActive, onClick) => (
        <Chip
            key={groupKey}
            size="small"
            clickable
            color={isActive ? 'primary' : 'default'}
            variant={isActive ? 'filled' : 'outlined'}
            label={
                <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 0.75}}>
                    <Box component="span">{label}</Box>
                    <Box
                        component="span"
                        sx={{
                            fontWeight: 700,
                            color: isActive ? 'inherit' : 'primary.main',
                        }}
                    >
                        {count}
                    </Box>
                </Box>
            }
            onClick={onClick}
            sx={{
                maxWidth: '100%',
                '& .MuiChip-label': {px: 1},
            }}
        />
    );

    return (
        <Box sx={{width: '100%', px: {xs: 2, md: 5}, pb: 2}}>
            <Box
                sx={{
                    position: 'sticky',
                    top: (theme) => theme.spacing(5),
                    zIndex: 10,
                    bgcolor: 'background.paper',
                    pt: 2,
                    pb: 2,
                    mb: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <TextField
                    fullWidth
                    size="small"
                    value={search}
                    onChange={handleSearchChange}
                    placeholder={t('searchPlaceholder')}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {borderRadius: 1},
                    }}
                />

                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        mt: 1.5,
                    }}
                >
                    {renderCategoryChip(
                        'all',
                        t('allCategories'),
                        totalFilteredCount,
                        activeCategory === null,
                        () => setActiveCategory(null),
                    )}
                    {expandedGroupedOptions.map((group) => renderCategoryChip(
                        group.group,
                        t(`categories.${group.group}`, {defaultValue: group.groupTitle}),
                        group.options.length,
                        activeCategory === group.group,
                        () => setActiveCategory(activeCategory === group.group ? null : group.group),
                    ))}
                </Box>
            </Box>

            <Box>
                {visibleGroups.length === 0 && (
                    <Box sx={{textAlign: 'center', py: 6}}>
                        <Typography variant="body1" color="text.secondary">
                            {t('emptySearch')}
                        </Typography>
                    </Box>
                )}

                {visibleGroups.map((group) => (
                    <CategorySection
                        key={group.group}
                        group={group.group}
                        groupTitle={group.groupTitle}
                        options={group.options}
                        resolveEntry={resolveEntry}
                        expandedId={expandedId}
                        onExpand={handleExpand}
                        onSelect={handleSelect}
                    />
                ))}
            </Box>
        </Box>
    );
};

export default DataSourceCatalog;
